import {faMagnifyingGlass} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {
    Button,
    Input,
    Segmented,
    Space,
    Table,
    TableColumnsType,
    TableColumnType,
    TablePaginationConfig,
    Typography,
} from 'antd';
import {SegmentedValue} from 'antd/lib/segmented';
import {FilterDropdownProps} from 'antd/lib/table/interface';
import {useMemo, useState} from 'react';
import {generatePath} from 'react-router-dom';

import {appRoute} from '../../../../../app-route';
import {NavigationLink} from '../../../../../layout/navigation-link/navigation-link';
import {PageCard} from '../../../../../layout/page-card/page-card';
import {FormattedNumber} from '../../../../../provider/locale/formatted-number';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {useReviewsByAspectsDetailed} from '../../../../../service/reivew-analysis/review-aspects/review-aspects-hook';
import {ReviewsByAspectsDetailedItemType} from '../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import {
    getStrongWeakAspectNames,
    getTonalityBandColor,
} from '../../pages/keywords-and-topics-page/aspects-tab/aspects-helper';
import {
    AspectsTableViewEnum,
    AspectStrengthEnum,
} from '../../pages/keywords-and-topics-page/aspects-tab/aspects-tab-type';
import {keywordsAndTopicsPageSizeOptions} from '../../pages/keywords-and-topics-page/keywords-and-topics-page-const';
import {AspectNameList} from '../aspect-name-list/aspect-name-list';

import * as styles from './aspects-by-locations-table.scss';

type AspectCellColumnType = {ids: Array<number>; title: string};

function renderTonalityValue(tonalityIndex: number | null | undefined): JSX.Element | string {
    if (typeof tonalityIndex !== 'number') {
        return '—';
    }

    return (
        <span className={styles.AspectsByLocations_indexValue} style={{color: getTonalityBandColor(tonalityIndex)}}>
            <FormattedNumber options={{maximumFractionDigits: 1}} value={tonalityIndex} />
        </span>
    );
}

// Match a pivot cell to its column by aspect id when the row carries ids; fall back to
// the localized title only when ids are unavailable.
function findAspectCellData(
    record: ReviewsByAspectsDetailedItemType,
    column: AspectCellColumnType
): ReviewsByAspectsDetailedItemType['aspectsData'][number] | undefined {
    return record.aspectsData.find((item) =>
        item.ids && item.ids.length > 0 ? item.ids.some((id) => column.ids.includes(id)) : item.aspect === column.title
    );
}

type CompanySearchColumnPropsType = {
    search: string;
    setSearch: (value: string) => void;
    searchPlaceholder: string;
};

// Server-side company name/address search wired into the antd column header. Kept at
// module scope (not inline in the render) so it isn't re-created as a nested component.
function getCompanySearchColumnProps({
    search,
    setSearch,
    searchPlaceholder,
}: CompanySearchColumnPropsType): Pick<
    TableColumnType<ReviewsByAspectsDetailedItemType>,
    'filteredValue' | 'filterIcon' | 'filterDropdown'
> {
    return {
        filteredValue: search ? [search] : null,
        filterIcon: (filtered: boolean) => (
            // eslint-disable-next-line no-undefined
            <FontAwesomeIcon icon={faMagnifyingGlass} style={{color: filtered ? '#1677ff' : undefined}} />
        ),
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}: FilterDropdownProps) => {
            function applySearch() {
                setSearch(((selectedKeys[0] as string) ?? '').trim());
                confirm();
            }

            function resetSearch() {
                setSelectedKeys([]);
                clearFilters?.();
                setSearch('');
                confirm();
            }

            return (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div
                    className={styles.AspectsByLocations_searchDropdown}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <Input
                        className={styles.AspectsByLocations_searchInput}
                        onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
                        onPressEnter={applySearch}
                        placeholder={searchPlaceholder}
                        value={(selectedKeys[0] as string) ?? ''}
                    />
                    <Space>
                        <Button
                            icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                            onClick={applySearch}
                            size="small"
                            type="primary"
                        >
                            <Locale stringKey="TEXT__SEARCH" />
                        </Button>
                        <Button onClick={resetSearch} size="small">
                            <Locale stringKey="TEXT__RESET" />
                        </Button>
                        <Button onClick={close} size="small" type="link">
                            <Locale stringKey="TEXT__CLOSE" />
                        </Button>
                    </Space>
                </div>
            );
        },
    };
}

// «Индекс тональности»: locations × aspects pivot, embedded on the «Аспекты» tab.
// Company name → single-location page; aspect columns are server-side sortable
// (sort=aspect_<id>) and the company column is searchable by name/address.
export function AspectsByLocationsTable(): JSX.Element {
    const {reviewsAnalysisAspectLocation} = appRoute;

    const {getLocalizedString} = useLocale();

    const [search, setSearch] = useState<string>('');

    const searchPlaceholder = getLocalizedString('REVIEWS_ANALYSIS__ASPECTS_BY_LOCATIONS__SEARCH_PLACEHOLDER');

    const {data, isLoading, pagination, sorter} = useReviewsByAspectsDetailed(search || null);
    const {total, pageSize, page, onChange: onPaginationChange} = pagination;
    const {setSorterOrder, getSortOrder} = sorter;

    const [viewMode, setViewMode] = useState<AspectsTableViewEnum>(AspectsTableViewEnum.TonalityIndex);

    const columns = useMemo<TableColumnsType<ReviewsByAspectsDetailedItemType>>(() => {
        const companyColumn = {
            title: <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_BY_LOCATIONS__COLUMN__COMPANY" />,
            key: 'company',
            fixed: 'left' as const,
            width: 320,
            ...getCompanySearchColumnProps({search, setSearch, searchPlaceholder}),
            render: (_value: unknown, record: ReviewsByAspectsDetailedItemType) => (
                <>
                    <NavigationLink
                        to={generatePath(reviewsAnalysisAspectLocation.path, {companyId: record.companyId})}
                    >
                        {record.companyName || record.companyId}
                    </NavigationLink>
                    {record.address ? (
                        <div>
                            <Typography.Text type="secondary">{record.address}</Typography.Text>
                        </div>
                    ) : null}
                </>
            ),
        };

        if (viewMode === AspectsTableViewEnum.StrongWeak) {
            return [
                companyColumn,
                {
                    title: <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__SENTIMENT_INDEX" />,
                    key: 'tonality_idx',
                    dataIndex: 'tonality_idx',
                    align: 'center' as const,
                    sorter: true,
                    sortOrder: getSortOrder('tonality_idx'),
                    render: (_value: unknown, record: ReviewsByAspectsDetailedItemType) =>
                        renderTonalityValue(record.tonalityIdx),
                },
                {
                    title: <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_BY_LOCATIONS__STRONG_SIDES" />,
                    key: 'strong-sides',
                    render: (_value: unknown, record: ReviewsByAspectsDetailedItemType) => (
                        <AspectNameList
                            names={getStrongWeakAspectNames(record.aspectsData, AspectStrengthEnum.Strong)}
                        />
                    ),
                },
                {
                    title: <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_BY_LOCATIONS__WEAK_SIDES" />,
                    key: 'weak-sides',
                    render: (_value: unknown, record: ReviewsByAspectsDetailedItemType) => (
                        <AspectNameList names={getStrongWeakAspectNames(record.aspectsData, AspectStrengthEnum.Weak)} />
                    ),
                },
            ];
        }

        const aspectColumns = (data?.aspects ?? []).map((column) => {
            const aspectId = column.ids[0];
            const columnKey = `aspect_${aspectId ?? column.title}`;

            return {
                title: column.title,
                key: columnKey,
                dataIndex: columnKey,
                align: 'center' as const,
                sorter: true,
                sortOrder: getSortOrder(columnKey),
                render: (_value: unknown, record: ReviewsByAspectsDetailedItemType) => {
                    const aspectData = findAspectCellData(record, column);

                    return aspectData ? renderTonalityValue(aspectData.currentPeriodData.tonalityIdx) : '—';
                },
            };
        });

        return [companyColumn, ...aspectColumns];
    }, [data?.aspects, viewMode, reviewsAnalysisAspectLocation.path, search, getSortOrder, searchPlaceholder]);

    return (
        <PageCard
            headerClassName={styles.AspectsByLocations_header}
            title={
                <>
                    <span>
                        <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__SENTIMENT_INDEX" />
                    </span>
                    <Segmented
                        onChange={(value: SegmentedValue) => setViewMode(value as AspectsTableViewEnum)}
                        options={[
                            {
                                label: <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_BY_LOCATIONS__VIEW__ALL_ASPECTS" />,
                                value: AspectsTableViewEnum.TonalityIndex,
                            },
                            {
                                label: <Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__VIEW__STRONG_WEAK" />,
                                value: AspectsTableViewEnum.StrongWeak,
                            },
                        ]}
                        value={viewMode}
                    />
                </>
            }
        >
            <Table<ReviewsByAspectsDetailedItemType>
                columns={columns}
                dataSource={data?.results ?? []}
                loading={isLoading}
                onChange={(_newPagination: TablePaginationConfig, _filters, tableSorter) => {
                    setSorterOrder(tableSorter);
                }}
                pagination={{
                    total,
                    pageSize,
                    current: page,
                    pageSizeOptions: keywordsAndTopicsPageSizeOptions,
                    size: 'default',
                    showSizeChanger: true,
                    onChange: onPaginationChange,
                    totalBoundaryShowSizeChanger: data?.count,
                    // eslint-disable-next-line react/no-unstable-nested-components
                    showTotal: (totalCount) => (
                        <Text
                            large
                            stringKey="TEXT__LIST_OF_COMPANIES"
                            valueMap={{
                                count: (
                                    <Text bolder key="count">
                                        {totalCount}
                                    </Text>
                                ),
                            }}
                        />
                    ),
                }}
                rowKey="companyId"
                scroll={{x: true}}
                size="middle"
            />
        </PageCard>
    );
}
